import React, { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';
import './PersonnelTable.css';

const PersonnelTable = () => {
  const [personnel, setPersonnel] = useState([]);
  const [grantList, setGrantList] = useState([]);
  const [showInactiveTable, setShowInactiveTable] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEditTimePopup, setShowEditTimePopup] = useState(false);
  const [showEditDefaultPaymentPopup, setShowEditDefaultPaymentPopup] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPersonnel();
    fetchGrantList();
  }, []);

  const fetchPersonnel = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/personnel`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setPersonnel(data);
    } catch (error) {
      console.error('Error fetching personnel:', error);
    }
  };

  const fetchGrantList = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/grants/grant-list`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const grantData = await response.json();
      setGrantList(grantData);
    } catch (error) {
      console.error('Error fetching grant list:', error);
    }
  };

  const handleEditTimeClick = (employee) => {
    setSelectedEmployee({
      ...employee,
      'Admin Time': parseInt(employee['Admin Time'] || 0),
      'Science Time': parseInt(employee['Science Time'] || 0),
    });
    setShowEditTimePopup(true);
  };

  const handleEditDefaultPaymentClick = (employee) => {
    let defaultPayment = {};
    if (employee['Default Payment']) {
      try {
        defaultPayment = JSON.parse(employee['Default Payment']);
      } catch (error) {
        console.error('Error parsing Default Payment:', error);
      }
    }

    setSelectedEmployee({
      ...employee,
      'Default Payment': defaultPayment
    });
    setShowEditDefaultPaymentPopup(true);
  };

  const handleTimeAllocationSubmit = async () => {
    const adminTime = parseFloat(selectedEmployee['Admin Time']);
    const scienceTime = parseFloat(selectedEmployee['Science Time']);

    if (adminTime + scienceTime !== 100) {
      alert('Admin Time (%) + Science Time (%) must equal 100%');
      return;
    }

    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/personnel/timeAllocation/${selectedEmployee['Employee Id']}`,
        {
          id: selectedEmployee['Employee Id'],
          adminTime: adminTime,
          scienceTime: scienceTime
        }
      );
      if (response.ok) {
        fetchPersonnel();
        setShowEditTimePopup(false);
      } else {
        alert('Failed to update time allocation.');
      }
    } catch (error) {
      console.error('Error updating time allocation:', error);
      alert('Error updating time allocation.');
    }
  };

  const handleEditDefaultPaymentSubmit = async () => {
    const defaultPayment = selectedEmployee['Default Payment'];

    const totalPercentage = Object.values(defaultPayment).reduce((sum, percentage) => sum + parseFloat(percentage), 0);
    if (Math.abs(totalPercentage - 100) > 0.01) {
      alert('The total percentage of default payment sources must add up to 100%.');
      return;
    }

    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/personnel/defaultPayment/${selectedEmployee['Employee Id']}`,
        {
          id: selectedEmployee['Employee Id'],
          defaultPayment: defaultPayment
        }
      );
      if (response.ok) {
        fetchPersonnel();
        setShowEditDefaultPaymentPopup(false);
      } else {
        alert('Failed to update default payment.');
      }
    } catch (error) {
      console.error('Error updating default payment:', error);
      alert('Error updating default payment.');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await api.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/personnel/import`,
          formData
        );

        if (response.ok) {
          alert('Employee information imported successfully');
          fetchPersonnel();
        } else {
          const errorMessage = await response.text();
          alert(`Failed to import employee information: ${errorMessage}`);
        }
      } catch (error) {
        console.error('Error importing employee information:', error);
        alert('Error importing employee information');
      }
    }
  };

  const toggleInactiveTable = () => {
    setShowInactiveTable(!showInactiveTable);
  };

  const formatDefaultPayment = (defaultPayment) => {
    if (!defaultPayment || defaultPayment === '') {
      return '';
    }
  
    if (typeof defaultPayment === 'string') {
      try {
        defaultPayment = JSON.parse(defaultPayment);
      } catch (error) {
        console.error('Error parsing Default Payment:', error);
        return 'Invalid format';
      }
    }
  
    if (typeof defaultPayment === 'object' && defaultPayment !== null) {
      const entries = Object.entries(defaultPayment);
      if (entries.length === 0) {
        return '';
      }
      return entries
        .map(([key, value]) => `${key}: ${value}%`)
        .join(', ');
    }
  
    return 'Invalid format';
  };

  // Split personnel into active and inactive
  const activePersonnel = personnel.filter(employee => employee['Employment status'] !== 'Inactive');
  const inactivePersonnel = personnel.filter(employee => employee['Employment status'] === 'Inactive');

  // Shared table rendering function
  const renderPersonnelTable = (employees, tableTitle) => (
    <div className="personnel-table-section">
      <h2 className="table-title">{tableTitle}</h2>
      <table className="personnel-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Employment Status</th>
            <th>Admin Time %</th>
            <th>Science Time %</th>
            <th>Default Payment %</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {employees.map(employee => (
            <tr key={employee['Employee Id']}>
              <td>{employee.Name}</td>
              <td>{employee['Employment status']}</td>
              <td>{employee['Admin Time']}</td>
              <td>{employee['Science Time']}</td>
              <td>{formatDefaultPayment(employee['Default Payment'])}</td>
              <td>
                <button onClick={() => handleEditTimeClick(employee)}>Edit Time</button>
                <button onClick={() => handleEditDefaultPaymentClick(employee)}>Edit Default Payment</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="personnel-table-container">
      {/* Active Employees Table */}
      {renderPersonnelTable(activePersonnel, "Active Employees")}

      {/* Toggle button for Inactive Employees */}
      <div className="toggle-inactive-container">
        <button 
          className="toggle-inactive-btn"
          onClick={toggleInactiveTable}
        >
          {showInactiveTable ? 'Hide Inactive Employees' : 'Show Inactive Employees'}
        </button>
      </div>

      {/* Inactive Employees Table (conditionally rendered) */}
      {showInactiveTable && renderPersonnelTable(inactivePersonnel, "Inactive Employees")}

      {/* File Import Controls (currently commented out) */}
      {/* <div className="import-container">
        <input
          type="file"
          accept=".csv,.json"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          ref={fileInputRef}
        />
        <button onClick={() => fileInputRef.current.click()}>Import Employee Information</button>
      </div> */}

      {/* Popups */}
      {showEditTimePopup && (
        <div className="popup">
          <h3>Edit Time Allocation</h3>
          <div>
            <label>Admin Time (%)</label>
            <select
              value={selectedEmployee['Admin Time']}
              onChange={(e) => setSelectedEmployee({ ...selectedEmployee, 'Admin Time': parseInt(e.target.value) })}
            >
              {Array.from({ length: 101 }, (_, i) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Science Time (%)</label>
            <select
              value={selectedEmployee['Science Time']}
              onChange={(e) => setSelectedEmployee({ ...selectedEmployee, 'Science Time': parseInt(e.target.value) })}
            >
              {Array.from({ length: 101 }, (_, i) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>
          <button onClick={handleTimeAllocationSubmit}>Save</button>
          <button onClick={() => setShowEditTimePopup(false)}>Cancel</button>
        </div>
      )}
      {showEditDefaultPaymentPopup && (
        <div className="popup">
          <h3>Edit Default Payment</h3>
          <p>Please fill in the percentage of each payment source. The sum of all payment source percentages should be 100%</p>
          {grantList.map((grant) => (
            <div key={grant}>
              <label>{grant}</label>
              <select
                value={selectedEmployee['Default Payment'][grant] || 0}
                onChange={(e) => {
                  const newPercentage = parseInt(e.target.value, 10) || 0;
                  setSelectedEmployee({
                    ...selectedEmployee,
                    'Default Payment': {
                      ...selectedEmployee['Default Payment'],
                      [grant]: newPercentage,
                    },
                  });
                }}
              >
                {Array.from({ length: 101 }, (_, i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
          ))}
          <button onClick={handleEditDefaultPaymentSubmit}>Save</button>
          <button onClick={() => setShowEditDefaultPaymentPopup(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
};

export default PersonnelTable;